import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Message, Channel } from '../../types/messages';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ChannelList from './ChannelList';
import CreateChannelModal from './CreateChannelModal';
import ChannelSettingsModal from './ChannelSettingsModal';
import ErrorDisplay from '../common/ErrorDisplay';
import LoadingDisplay from '../common/LoadingDisplay';
import { Plus, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import { useSchool } from '../../contexts/SchoolContext';
import { useSearchParams } from 'react-router-dom';
import { NotificationService } from '../../services/NotificationService';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import FileService from '../../services/FileService';

export default function MessagesPage() {
  useDocumentTitle('Messages');
  
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const { selectedSchool } = useSchool();
  const [directMessageUser, setDirectMessageUser] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const messageInputRef = React.useRef<HTMLDivElement>(null);

  const handleStartDirectMessage = (userId: string, userName: string) => {
    setDirectMessageUser({ id: userId, name: userName });
    setSelectedChannelId(undefined);
  };

  // Scroll to message input when messages change
  useEffect(() => {
    if (messages.length > 0 && messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 200);
    }
  }, [messages]);

  // Handle scrolling when AI summary loads
  const handleSummaryLoaded = () => {
    if (messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  };

  useEffect(() => {
    const loadUserDetails = async () => {
      if (!userId) return;

      try {
        const userDoc = await getDocs(
          query(collection(db, 'users'), where('__name__', '==', userId))
        );
        
        if (!userDoc.empty) {
          const userData = userDoc.docs[0].data();
          setDirectMessageUser({
            id: userId,
            name: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User'
          });
        }
      } catch (error) {
        console.error('Error loading user details:', error);
      }
    };

    loadUserDetails();
  }, [userId]);

  const loadChannels = () => {
    if (!selectedSchool) return;

    setLoading(true);
    setError(null);

    const unsubscribeChannels = onSnapshot(
      query(
        collection(db, 'channels'),
        where('schoolId', '==', selectedSchool.id),
        where('members', 'array-contains', auth.currentUser?.uid),
        orderBy('createdAt', 'desc')
      ),
      (snapshot) => {
        const channelData: Channel[] = [];
        snapshot.forEach((doc) => {
          channelData.push({ id: doc.id, ...doc.data() } as Channel);
        });
        setChannels(channelData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error('Error loading channels:', error);
        setError('Failed to load channels. Please check your connection and try again.');
        setLoading(false);
      }
    );

    return unsubscribeChannels;
  };

  useEffect(() => {
    const unsubscribe = loadChannels();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedSchool]);

  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  const loadMessages = () => {
    if (!selectedChannelId && !directMessageUser) return;

    console.log('Subscribing to messages for:', selectedChannelId || 'direct messages');
    setMessagesLoading(true);
    setMessagesError(null);

    const messagesQuery = selectedChannelId 
      ? query(
          collection(db, `channels/${selectedChannelId}/messages`),
          orderBy('createdAt', 'asc')
        )
      : query(
          collection(db, 'messages'),
          where('participants', 'array-contains', auth.currentUser?.uid),
          orderBy('createdAt', 'asc')
        );

    const unsubscribeMessages = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messageData: Message[] = [];
        snapshot.forEach((doc) => {
          const message = { id: doc.id, ...doc.data() } as Message;
          if (!selectedChannelId) {
            const otherUser = directMessageUser?.id;
            if (
              (message.senderId === auth.currentUser?.uid && message.receiverId === otherUser) ||
              (message.senderId === otherUser && message.receiverId === auth.currentUser?.uid)
            ) {
              messageData.push(message);
            }
          } else {
            messageData.push(message);
          }
        });

        console.log('Received messages:', messageData.length);
        setMessages(messageData);
        setMessagesLoading(false);
        setMessagesError(null);
      },
      (error) => {
        console.error('Error loading messages:', error);
        setMessagesError('Failed to load messages. Please check your connection and try again.');
        setMessagesLoading(false);
      }
    );

    return unsubscribeMessages;
  };

  useEffect(() => {
    const unsubscribe = loadMessages();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedChannelId, directMessageUser]);

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!auth.currentUser || !selectedSchool) return;

    console.log('MessagesPage: handleSendMessage called with attachments:', attachments?.length || 0);

    try {
      let uploadedFiles: any[] = [];

      // Upload attachments if any
      if (attachments && attachments.length > 0) {
        console.log('MessagesPage: Uploading files:', attachments.length);
        try {
          uploadedFiles = await FileService.uploadFiles(
            attachments,
            'message',
            selectedSchool.id,
            auth.currentUser.uid,
            undefined, // parentId will be set after message is created
            false,
            () => {} // progress callback
          );
          console.log('MessagesPage: Files uploaded successfully:', uploadedFiles.length);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          
          // For CORS errors in development, allow sending text-only message
          if (uploadError instanceof Error && 
              (uploadError.message.includes('CORS') || 
               uploadError.toString().includes('net::ERR_FAILED') ||
               uploadError.toString().includes('blocked by CORS'))) {
            
            toast.error('File upload failed due to CORS policy. Sending text message only. (Development environment issue)');
            console.log('MessagesPage: Continuing with text-only message due to CORS error');
            uploadedFiles = []; // Clear files and continue
          } else {
            // For other errors, re-throw to abort the message send
            throw uploadError;
          }
        }
      }

      const messageData = {
        content,
        senderId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        ...(uploadedFiles.length > 0 && { attachments: uploadedFiles }),
      };

      let messageRef;
      let notificationPromises = [];

      if (selectedChannelId) {
        messageRef = await addDoc(collection(db, `channels/${selectedChannelId}/messages`), {
          ...messageData,
          channelId: selectedChannelId,
        });

        // Update the channel's lastMessage field
        await updateDoc(doc(db, 'channels', selectedChannelId), {
          lastMessage: {
            content,
            senderId: auth.currentUser.uid,
            createdAt: new Date().toISOString()
          },
          updatedAt: serverTimestamp()
        });

        // Create notifications for channel members (excluding sender)
        const selectedChannel = channels.find(c => c.id === selectedChannelId);
        if (selectedChannel?.members) {
          const recipients = selectedChannel.members.filter(memberId => memberId !== auth.currentUser?.uid);
          notificationPromises = recipients.map(recipientId =>
            NotificationService.createNotification({
              type: 'message',
              title: `New message in #${selectedChannel.name}`,
              message: content.length > 50 ? `${content.substring(0, 50)}...` : content,
              priority: 'medium',
              recipientId,
              actionUrl: `/messages`,
              read: false,
              metadata: {
                messageId: messageRef.id
              }
            })
          );
        }
      } else if (directMessageUser) {
        messageRef = await addDoc(collection(db, 'messages'), {
          ...messageData,
          receiverId: directMessageUser.id,
          participants: [auth.currentUser.uid, directMessageUser.id],
        });

        // Create notification for direct message recipient
        notificationPromises = [
          NotificationService.createNotification({
            type: 'message',
            title: 'New direct message',
            message: content.length > 50 ? `${content.substring(0, 50)}...` : content,
            priority: 'medium',
            recipientId: directMessageUser.id,
            actionUrl: `/messages?userId=${auth.currentUser.uid}`,
            read: false,
            metadata: {
              messageId: messageRef.id
            }
          })
        ];
      }

      // Send all notifications
      if (notificationPromises.length > 0) {
        await Promise.all(notificationPromises);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Provide specific error messages for different types of failures
      if (error instanceof Error) {
        if (error.message.includes('CORS') || error.message.includes('net::ERR_FAILED')) {
          toast.error('File upload failed due to CORS policy. This is a development environment issue.');
        } else if (error.message.includes('storage')) {
          toast.error('File upload failed. Please try again.');
        } else {
          toast.error('Failed to send message: ' + error.message);
        }
      } else {
        toast.error('Failed to send message');
      }
      
      // Re-throw the error so MessageInput doesn't clear the form
      throw error;
    }
  };

  const selectedChannel = channels.find(c => c.id === selectedChannelId);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <LoadingDisplay message="Loading messages..." size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <ErrorDisplay 
            error={error} 
            onRetry={() => loadChannels()} 
            size="lg" 
          />
        </div>
      </AppLayout>
    );
  }

  if (!selectedSchool) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Please select a school to view channels</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex h-full">
        <ChannelList
          channels={channels}
          selectedChannelId={selectedChannelId}
          onSelectChannel={(channelId) => {
            setSelectedChannelId(channelId);
            setDirectMessageUser(null);
          }}
          onCreateChannel={() => setShowCreateModal(true)}
          onStartDirectMessage={handleStartDirectMessage}
        />
        
        <div className="flex-1 flex flex-col min-h-0">
          {(selectedChannelId || directMessageUser) ? (
            <>
              <div className="h-16 border-b border-border-light px-6 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">
                    {selectedChannel ? selectedChannel.name : directMessageUser?.name}
                  </h2>
                  {selectedChannel?.description && (
                    <p className="text-sm text-text-secondary">{selectedChannel.description}</p>
                  )}
                </div>
                {selectedChannel && (
                  <button 
                    onClick={() => setShowSettingsModal(true)}
                    className="p-2 text-text-secondary hover:text-primary-500 transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                )}
              </div>

              {messagesLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <LoadingDisplay message="Loading messages..." />
                </div>
              ) : messagesError ? (
                <div className="flex-1 flex items-center justify-center">
                  <ErrorDisplay 
                    error={messagesError} 
                    onRetry={() => loadMessages()} 
                  />
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  currentUserId={auth.currentUser?.uid || ''}
                  isChannel={!!selectedChannelId}
                  onSummaryLoaded={handleSummaryLoaded}
                />
              )}

              <div ref={messageInputRef}>
                <MessageInput 
                  onSend={handleSendMessage} 
                  onAttachmentClick={() => {
                    // Scroll to bottom when attachment button is clicked
                    setTimeout(() => {
                      messageInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                    }, 100);
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                           transition-colors flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Channel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateChannelModal 
          onClose={() => setShowCreateModal(false)}
          schoolId={selectedSchool.id}
        />
      )}

      {showSettingsModal && selectedChannel && (
        <ChannelSettingsModal
          channel={selectedChannel}
          onClose={() => setShowSettingsModal(false)}
          onDelete={() => {
            setSelectedChannelId(undefined);
            setShowSettingsModal(false);
          }}
        />
      )}
    </AppLayout>
  );
}